import { ReactCta as CTA } from '@/components/react/ui/ReactCta';
import { ReactHeading } from '@/components/react/ui/ReactHeading';
import { ReactImage } from '@/components/react/ui/ReactImage';
import type { ImageMetadata } from 'astro';
import type { ComponentProps } from 'astro/types';
import { cva, cx, type VariantProps } from 'cva';
import type React from 'react';
import { twMerge } from 'tailwind-merge';

export const promoVariants = cva({
  base: 'rounded-lg bg-light-foam-50',
  variants: {
    variant: {
      'vertical-top': 'flex-col-reverse',
      'vertical-bottom': 'flex-col',
      horizontal: 'md:min-w-211.25 md:h-102',
    },
    imageBg: {
      true: null,
      false: null,
    },
    width: {
      fullWidth: 'w-full',
      unset: null,
    },
  },
  defaultVariants: {
    variant: 'vertical-bottom',
  },
  compoundVariants: [
    {
      variant: 'vertical-top',
      width: 'unset',
      class: 'max-w-54.5 md:max-w-102.5',
    },
    {
      variant: 'vertical-bottom',
      width: 'unset',
      class: 'max-w-54.5 md:max-w-102.5',
    },
    {
      variant: 'vertical-top',
      imageBg: true,
      class: 'min-h-104.25 md:min-h-143 justify-end',
    },
    {
      variant: 'vertical-bottom',
      imageBg: true,
      class: 'min-h-104.25 md:min-h-143 justify-end',
    },
  ],
});

export interface PromoCardProps extends VariantProps<typeof promoVariants> {
  heading: string;
  content?: string;
  eyebrow?: string;
  cta?: ComponentProps<typeof CTA>
    & Required<Pick<ComponentProps<typeof CTA>, 'href'>>;
  image: {
    src: ImageMetadata;
    alt: string;
    asBackground?: boolean;
  };
  className?: string;
}

export const ReactPromoCard: React.FC<PromoCardProps> = ({
  heading,
  variant,
  content,
  width = 'unset',
  cta,
  image,
  eyebrow,
  className,
}) => {
  return (
    <div
      className={twMerge(
        'relative flex gap-6 p-4 lg:p-6',
        promoVariants({ variant, imageBg: !!image?.asBackground, width }),
        className,
      )}
    >
      <ReactImage
        src={image.src.src}
        alt={image.alt}
        className={cx({
          'aspect-square':
            !image.asBackground
            && variant !== 'horizontal'
            && width !== 'fullWidth',
          'object-cover': width === 'fullWidth',
          'aspect-auto': !image.asBackground && variant === 'horizontal',
          'h-73.75 w-full rounded-sm md:h-72 md:max-h-72 lg:h-90 lg:max-h-90':
            !image.asBackground,
          'max-h-46.5': !image.asBackground && width !== 'fullWidth',
          'absolute inset-0 h-full w-full rounded-lg object-cover object-center':
            image.asBackground,
        })}
        layout="fullWidth"
      />

      <div className="text-blurb-text-subtle relative flex flex-col justify-center gap-6">
        <div className="flex flex-col justify-start gap-2">
          {eyebrow && (
            <div className="text-blurb-text-subtle text-sm">{eyebrow}</div>
          )}
          <ReactHeading text={heading} size="h3" />
          {content && <p>{content}</p>}
        </div>
        {cta && (
          <CTA
            className={variant === 'horizontal' ? '' : 'text center'}
            {...cta}
            size="small"
            type="button"
          />
        )}
      </div>
    </div>
  );
};
